import NoteService from "@/services/Notes";
import type { NoteCreatePayload } from "@/services/Notes";
import type { Note, PatchedNote } from "@/types/api/data-contracts";
import { ref } from "vue";

const selectedNote = ref<Note | null>(null);

export const useNotes = (props?: {
    targetType: string;
    targetUuid: string;
}) => {
    const loading = ref(false);
    const notes = ref<Note[]>([]);

    const noteService = NoteService.getInstance();

    const fetchNotes = async () => {
        if (!props?.targetUuid || !props?.targetType) return;

        loading.value = true;
        try {
            const res = await noteService.listEntityNotes({
                target_type: props.targetType,
                target_uuid: props.targetUuid,
            });
            notes.value = res.data?.results ?? [];
        } finally {
            loading.value = false;
        }
    };

    const createNote = async (
        payload: Omit<NoteCreatePayload, "target_type" | "target_uuid">
    ) => {
        if (!props?.targetUuid || !props?.targetType) return undefined;

        const res = await noteService.createEntityNote({
            ...payload,
            target_type: props.targetType,
            target_uuid: props.targetUuid,
        });
        return res.data;
    };

    const updateNote = async (noteUuid: string, payload: PatchedNote) => {
        const res = await noteService.updateEntityNote(noteUuid, payload);
        return res.data;
    };

    const deleteNote = async (): Promise<boolean> => {
        if (!selectedNote.value?.uuid) return false;

        await noteService.deleteEntityNote(selectedNote.value.uuid);
        return true;
    };

    const setSelectedNote = (note: Note | null) => {
        selectedNote.value = note;
    };

    const clearSelection = () => {
        selectedNote.value = null;
    };

    return {
        loading,
        notes,
        selectedNote,
        fetchNotes,
        createNote,
        updateNote,
        deleteNote,
        setSelectedNote,
        clearSelection,
    };
};
